import type { ComponentProps, MutableRefObject } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import type { PropGetters } from 'downshift';
import type { DisplayProps } from 'styled-system';
import { display, space, themeGet } from 'styled-system';

import type { ProductSuggestionItem } from '../../../global-header/components/toolbar/types';
import type { SpaceProps } from '../../theme/system-types';
import RecentSearchesList from './RecentSearchesList';
import SuggestionsList from './SuggestionsList';
import SuggestionsListContainer from './SuggestionsListContainer';
import SuggestionsProductList from './SuggestionsProductList';
import type {
  ProductSuggestionItemProps,
  RecentSearches,
  SearchSuggestionsItemProps,
  SuggestionGetItemProps,
  SuggestionItem,
} from './types';

interface SuggestionsListProps {
  suggestions: SuggestionItem[];
  recentSearches: Nullable<RecentSearches>;
  listItems: Array<SuggestionItem['name']>;
  open: boolean;
  timeout: number;
  inputValue: string;
  highlightedIndex: number;
  menuProps: ReturnType<PropGetters<SuggestionItem>['getMenuProps']>;
  getItemProps: SuggestionGetItemProps<SearchSuggestionsItemProps | ProductSuggestionItemProps>;
  isBodyOverlayVisible: boolean;
  hasExtraPadding: boolean;
  exitSuggestions: (shouldClearInput: boolean) => void;
  productSuggestions: ProductSuggestionItem[];
  inputContainerRef: MutableRefObject<HTMLDivElement>;
  shopName?: string;
}

const VerticalLine = styled.div<DisplayProps & SpaceProps>`
  ${display};
  ${space};
  border-left: ${themeGet('borderWidths.small')} solid ${themeGet('colors.sys.neutral.border.weak')};
`;

const verticalLineStyles: ComponentProps<typeof VerticalLine> = {
  display: ['none', null, 'block'],
  mx: [null, null, 'sp_24', 'sp_32'],
};

const SuggestionsBox = ({
  menuProps,
  hasExtraPadding,
  isBodyOverlayVisible,
  productSuggestions,
  timeout,
  inputContainerRef,
  recentSearches,
  ...props
}: SuggestionsListProps) => {
  const isRecentSearchesOpen = Boolean(recentSearches?.flags.isOpen);
  const isSingleColumn = isRecentSearchesOpen || productSuggestions.length === 0;

  return (
    <SuggestionsListContainer
      {...menuProps}
      hasExtraPadding={hasExtraPadding}
      isSingleColumn={isSingleColumn}
      timeout={timeout}
      isBodyOverlayVisible={isBodyOverlayVisible}
      data-testid="suggestion-list-item"
      inputContainerRef={inputContainerRef}
    >
      {isRecentSearchesOpen ? (
        <RecentSearchesList
          listItems={recentSearches.listItems}
          recentSearches={recentSearches}
          getItemProps={props.getItemProps}
          highlightedIndex={props.highlightedIndex}
        />
      ) : (
        <SuggestionsList {...props} />
      )}

      {!isSingleColumn && (
        <>
          <VerticalLine {...verticalLineStyles} />
          <SuggestionsProductList
            productSuggestions={productSuggestions}
            getItemProps={props.getItemProps}
            highlightedIndex={props.highlightedIndex}
            productsStartIndex={props.suggestions.length || 0}
          />
        </>
      )}
    </SuggestionsListContainer>
  );
};

export default SuggestionsBox;
